import { X, Eye, Image as ImageIcon } from 'lucide-react';
import { formatTextWithStyles } from '../../utils/formatText';
import { useTranslation } from '../../i18n/useTranslation';
import Modal from '../core/Modal';

/**
 * ArticlePreviewModal - Preview the Article as readers will see it
 * Shows the final layout in a modal window for the author to review
 */
const ArticlePreviewModal = ({
  isOpen,
  onClose,
  formData,
  heroImageDisplay,
  heroImageCredit,
  inlineImages,
  groupName
}) => {
  const { t } = useTranslation('articles');
  // Image size options: S = 33%, M = 66%, L = 100%
  const IMAGE_SIZES = {
    S: '33%',
    M: '66%',
    L: '100%'
  };

  // Render content with inline images (support both [IMAGE:] and [IMG:] formats)
  const renderContentWithImages = (content, images) => {
    if (!content) return null;

    // Check if content has image markers
    const hasImageMarkers = /\[(IMAGE|IMG):[^\]]+\]/.test(content);

    if (!hasImageMarkers) {
      // No image markers - use formatting with bold/italic/heading support
      return formatTextWithStyles(content, 'prev-', {
        fontSize: '16px',
        lineHeight: '1.7',
        color: '#374151',
        marginBottom: '16px'
      });
    }

    // Has image markers - parse and render
    const parts = content.split(/\[(?:IMAGE|IMG):([^\]]+)\]/);

    return parts.map((part, index) => {
      // Even indices are text content
      if (index % 2 === 0) {
        if (!part.trim()) return null;
        return formatTextWithStyles(part, `prev-${index}-`, {
          fontSize: '16px',
          lineHeight: '1.7',
          color: '#374151',
          marginBottom: '16px'
        });
      } else {
        // Odd indices are image IDs
        const imageId = part;
        const image = images.find(img => img.id === imageId);

        if (image) {
          const imageWidth = IMAGE_SIZES[image.size] || '100%';
          return (
            <div key={`img-${index}`} style={{ margin: '24px 0' }}>
              <img
                src={image.displayUrl}
                alt={image.caption || ''}
                loading="lazy"
                style={{
                  width: imageWidth,
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '8px'
                }}
              />
              {(image.caption || 'credit' in image) && (
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', maxWidth: imageWidth }}>
                  {image.caption && <div style={{ fontStyle: 'italic' }}>{image.caption}</div>}
                  {image.credit ? (
                    <div>{image.credit}</div>
                  ) : 'credit' in image ? (
                    <div>{t('image:credit.uncredited')}</div>
                  ) : null}
                </div>
              )}
            </div>
          );
        }
        return (
          <div key={`img-${index}`} style={{
            margin: '24px 0',
            padding: '16px',
            backgroundColor: '#f3f4f6',
            borderRadius: '8px',
            textAlign: 'center',
            color: '#6b7280',
            fontSize: '14px'
          }}>
            {t('image_not_available')}
          </div>
        );
      }
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      panelStyle={{
        backgroundColor: '#EDF2F8',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '900px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        margin: '20px'
      }}
    >
        {/* Preview Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 24px',
          backgroundColor: '#024B99',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Eye size={20} />
            <span style={{ fontSize: '16px', fontWeight: '600' }}>
              {t('preview_modal_title')}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            <X size={20} color="white" />
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '32px'
        }}>
          {/* Article Card Preview */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}>
            {/* Hero Image */}
            {heroImageDisplay ? (
              <div style={{
                width: '100%',
                aspectRatio: '16 / 9',
                overflow: 'hidden'
              }}>
                <img
                  src={heroImageDisplay}
                  alt={formData.title || 'Article hero image'}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
            ) : (
              <div style={{
                width: '100%',
                aspectRatio: '16 / 9',
                backgroundColor: '#e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <ImageIcon size={64} color="#9ca3af" />
              </div>
            )}

            {/* Image Caption + Credit */}
            {(formData.image_caption || heroImageCredit || heroImageDisplay) && (
              <div style={{
                padding: '8px 24px',
                backgroundColor: '#f9fafb',
                borderBottom: '1px solid #e5e7eb'
              }}>
                {formData.image_caption && (
                  <p style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    fontStyle: 'italic',
                    margin: 0
                  }}>
                    {formData.image_caption}
                  </p>
                )}
                {heroImageCredit ? (
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                    {heroImageCredit}
                  </p>
                ) : heroImageDisplay ? (
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                    {t('image:credit.uncredited')}
                  </p>
                ) : null}
              </div>
            )}

            {/* Article Content */}
            <div style={{ padding: '24px' }}>
              {/* Group Name */}
              {groupName && (
                <p style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#024B99',
                  textTransform: 'uppercase',
                  marginBottom: '8px'
                }}>
                  {groupName}
                </p>
              )}

              {/* Title */}
              <h1 style={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#111827',
                lineHeight: '1.2',
                marginBottom: '16px'
              }}>
                {formData.title || t('untitled_article')}
              </h1>

              {/* Introduction/Summary */}
              {formData.summary && (
                <p style={{
                  fontSize: '18px',
                  color: '#4b5563',
                  lineHeight: '1.6',
                  marginBottom: '24px',
                  fontWeight: '500'
                }}>
                  {formData.summary}
                </p>
              )}

              {/* Divider */}
              <div style={{
                height: '1px',
                backgroundColor: '#e5e7eb',
                margin: '24px 0'
              }} />

              {/* Main Content */}
              <div>
                {formData.content ? (
                  renderContentWithImages(formData.content, inlineImages)
                ) : (
                  <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                    {t('no_content_yet')}
                  </p>
                )}
              </div>

              {/* Tags */}
              {formData.article_tags && formData.article_tags.length > 0 && (
                <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {formData.article_tags.map((tag, index) => (
                      <span
                        key={index}
                        style={{
                          padding: '4px 12px',
                          backgroundColor: '#e0f2fe',
                          color: '#0369a1',
                          borderRadius: '16px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Close Button at bottom */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '12px 32px',
              background: '#024B99',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'opacity 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
          >
            {t('close_preview')}
          </button>
        </div>
    </Modal>
  );
};

export default ArticlePreviewModal;
